import type { FC } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { UpgradeEditionButton } from '@confluence/change-edition/entry-points/UpgradeEditionButton.ts';
import { ADMIN_NEW_FEATURES } from '@confluence/named-routes/entry-points/routes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import approvalsImageLight from '../../assets/approvalsImageLight.svg';
import approvalsImageDark from '../../assets/approvalsImageDark.svg';

const SUPPORT_URL =
	'https://support.atlassian.com/confluence-cloud/docs/request-and-manage-approvals-in-confluence/';
const NEW_FEATURES_URL = ADMIN_NEW_FEATURES.toUrl();

const i18n = defineMessages({
	heading: {
		id: 'content-state.menu.v2.approvals-upsell.heading',
		defaultMessage: 'Start requesting approval',
		description:
			'The heading of a modal opened from the Start approval menu item in the editor status menu. The modal explains how to enable or try approvals before requesting page sign-off.',
	},
	standardDescription: {
		id: 'content-state.menu.v2.approvals-upsell.standard-description',
		defaultMessage:
			'Upgrade to Premium to request sign-off on Confluence pages and track approver decisions.',
		description:
			'Description shown in the Start approval upsell modal when the site is not on Confluence Premium. It explains that Premium is required before users can request approvals.',
	},
	premiumDescription: {
		id: 'content-state.menu.v2.approvals-upsell.premium-description',
		defaultMessage:
			'Enable approvals to request sign-off on Confluence pages and track approver decisions.',
		description:
			'Description shown in the Start approval upsell modal when the site is already on Confluence Premium but approvals are not enabled for the site.',
	},
	learnMore: {
		id: 'content-state.menu.v2.approvals-upsell.learn-more',
		defaultMessage: 'Learn more',
		description:
			'Button label in the Start approval upsell modal. Clicking it opens documentation about approvals in a new browser tab.',
	},
	enableInSettings: {
		id: 'content-state.menu.v2.approvals-upsell.enable-in-settings',
		defaultMessage: 'Enable in Settings',
		description:
			'Primary button label in the Start approval upsell modal for Premium sites. Clicking it opens the New features site settings page in a new browser tab.',
	},
});

const styles = cssMap({
	illustrationHeader: {
		position: 'relative',
		display: 'flex',
		justifyContent: 'center',
		overflow: 'hidden',
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
	},
	closeButton: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
});

type ApprovalsUpsellModalProps = {
	onClose: () => void;
};

const openInNewTab = (url: string) => {
	window.open(url, '_blank', 'noopener,noreferrer');
};

export const ApprovalsUpsellModal: FC<ApprovalsUpsellModalProps> = ({ onClose }) => {
	const { Modal } = useChoreographerAssets();
	const { edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;

	const openLearnMore = useCallback(() => {
		openInNewTab(SUPPORT_URL);
	}, []);

	const openNewFeaturesSettings = useCallback(() => {
		openInNewTab(NEW_FEATURES_URL);
	}, []);

	return (
		<ModalTransition>
			<Modal
				onClose={onClose}
				width="small"
				testId="approvals-upsell-modal"
				messageType="engagement"
				messageId="content-state-approvals-upsell-modal"
			>
				<Box xcss={styles.illustrationHeader}>
					<Image src={approvalsImageLight} srcDark={approvalsImageDark} alt="" />
					<Box xcss={styles.closeButton}>
						<CloseButton onClick={onClose} />
					</Box>
				</Box>
				<ModalHeader>
					<ModalTitle>
						<FormattedMessage {...i18n.heading} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<Text as="p">
						<FormattedMessage
							{...(isPremium ? i18n.premiumDescription : i18n.standardDescription)}
						/>
					</Text>
				</ModalBody>
				<ModalFooter>
					<Inline space="space.100">
						<Button appearance="subtle" onClick={openLearnMore}>
							<FormattedMessage {...i18n.learnMore} />
						</Button>
						{isPremium ? (
							<Button appearance="primary" onClick={openNewFeaturesSettings}>
								<FormattedMessage {...i18n.enableInSettings} />
							</Button>
						) : (
							<UpgradeEditionButton
								analyticsProps={{
									actionSubjectId: 'approvalsTryItFreeButton',
									attributes: { currentEdition: edition },
									source: 'approvalsStartApprovalUpsell',
								}}
								appearance="primary"
								targetEdition={ConfluenceEdition.PREMIUM}
							/>
						)}
					</Inline>
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
